// src/components/admin/StaffPerformancePanel.js
// Rendement des commerciaux — vue direction
// Maxula — AbodyStudio Limited
//
// Le pendant de StaffSalesPanel : celui-ci montre TOUS les commerciaux côte à
// côte, avec leur portefeuille, leur travail de validation et les ventes de
// leurs marchands.

import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiTrendingUp, FiUsers, FiCheckSquare, FiClock, FiChevronDown,
  FiChevronRight, FiAlertTriangle, FiLoader,
} from 'react-icons/fi';
import API from '../../services/api';

const fmt = (v) => (Number(v) || 0).toLocaleString('fr-FR');

const Stat = ({ icon: Icon, label, value, suffix, tone = 'gray' }) => {
  const tones = {
    gray: 'text-gray-700', green: 'text-green-600',
    amber: 'text-amber-600', pink: 'text-[#e11d7b]',
  };
  return (
    <div className="bg-white border border-gray-200 rounded-xl p-4">
      <div className="flex items-center gap-2 text-gray-500 text-xs mb-1">
        <Icon size={14} /> {label}
      </div>
      <div className={`text-2xl font-bold ${tones[tone]}`}>
        {value}{suffix ? <span className="text-sm font-normal text-gray-400"> {suffix}</span> : null}
      </div>
    </div>
  );
};

const StaffPerformancePanel = () => {
  const [data, setData] = useState(null);
  // v1.400.0 — 'month' = du 1er du mois a aujourd'hui (defaut)
  const [days, setDays] = useState('month');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openId, setOpenId] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const res = await API.get('/admin/staff/performance', { params: days === 'month' ? { period: 'month' } : { days } });
        if (!alive) return;
        setData(res?.data?.data || null);
        setError(null);
      } catch (e) {
        if (!alive) return;
        setData(null);
        // On NOMME le serveur interrogé. Sans lui, un 404 ne dit pas s'il vient
        // du serveur local ou de la production — deux machines, deux copies des
        // fichiers, et c'est la confusion qui coûte le plus de temps ici.
        const cible = API?.defaults?.baseURL || '(inconnu)';
        setError(
          e?.response?.status === 404
            ? `Route /admin/staff/performance absente sur ${cible} — ce serveur-là n'est pas à jour. Vérifiez ${cible}/health/routes : adminRoutes doit valoir 61.`
            : (e?.response?.data?.message || `Chargement impossible depuis ${cible}`)
        );
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, [days]);

  // ===================================================================
  // TOTAUX DES VIGNETTES.
  //
  // Le chiffre d'affaires et le nombre de commandes viennent du SERVEUR, ou
  // chaque commande n'est comptee qu'une fois. Ils NE sont plus additionnes
  // depuis les lignes du tableau : un marchand suivi par trois commerciaux
  // fait apparaitre la meme vente sur trois lignes — legitime ligne par
  // ligne, faux une fois additionne. Une seule vente de 500 MAD affichait
  // ainsi 1 500 MAD.
  //
  // Les annonces, elles, restent additionnees : elles sont attribuees a UN
  // moderateur precis (moderatedById), donc sans recouvrement possible.
  // ===================================================================
  const parLigne = (data?.staff || []).reduce((acc, s) => ({
    validated: acc.validated + s.validatedDeals,
    pending: acc.pending + s.pendingDeals,
  }), { validated: 0, pending: 0 });

  const totals = {
    // Repli sur l'addition si le serveur est plus ancien et ne renvoie pas
    // encore `totals` : mieux vaut un chiffre trop haut qu'un ecran vide.
    orders: data?.totals?.orders ?? (data?.staff || []).reduce((a, s) => a + s.ordersCount, 0),
    revenue: data?.totals?.revenue ?? (data?.staff || []).reduce((a, s) => a + s.revenue, 0),
    validated: parLigne.validated,
    pending: parLigne.pending,
  };

  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div>
          <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <FiTrendingUp className="text-[#e11d7b]" /> Rendement des commerciaux
          </h2>
          <p className="text-sm text-gray-500">
            Ce que chaque commercial suit, valide, et ce que son portefeuille a vendu.
          </p>
        </div>
        <select
          value={days}
          onChange={(e) => setDays(e.target.value === 'month' ? 'month' : Number(e.target.value))}
          className="border rounded-lg px-3 py-2 text-sm"
        >
          <option value="month">Ce mois-ci</option>
          <option value={7}>7 derniers jours</option>
          <option value={30}>30 derniers jours</option>
          <option value={90}>90 derniers jours</option>
          <option value={365}>12 derniers mois</option>
        </select>
      </div>

      {loading && (
        <div className="p-8 flex items-center justify-center gap-2 text-gray-500">
          <FiLoader className="animate-spin" /> Chargement…
        </div>
      )}

      {!loading && error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">{error}</div>
      )}

      {!loading && !error && data && (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
            <Stat icon={FiTrendingUp} label="Ventes encaissées" value={fmt(totals.revenue)} suffix="MAD" tone="green" />
            <Stat icon={FiUsers} label="Commandes" value={fmt(totals.orders)} />
            <Stat icon={FiCheckSquare} label="Annonces traitées" value={fmt(totals.validated)} tone="pink" />
            <Stat icon={FiClock} label="En attente de validation" value={fmt(totals.pending)} tone="amber" />
          </div>

          {/* Le chiffre qui manque toujours dans ce genre d'écran : les marchands
              que personne ne suit. C'est le seul qui signale un oubli. */}
          {data.unassignedMerchants > 0 && (
            <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800 flex items-start gap-2">
              <FiAlertTriangle className="mt-0.5 shrink-0" />
              <span>
                <b>{data.unassignedMerchants} marchand{data.unassignedMerchants > 1 ? 's' : ''}</b> ne
                {data.unassignedMerchants > 1 ? ' sont' : " n'est"} suivi{data.unassignedMerchants > 1 ? 's' : ''} par
                aucun commercial (sur {fmt(data.totalMerchants)}).{' '}
                <Link to="/admin/staff" className="underline font-semibold">Répartir les marchands</Link>
              </span>
            </div>
          )}

          {/* v1.401.0 — VENTES PAR COMMERCIAL. La colonne « Ventes du
              portefeuille » affiche le MEME total pour tous des que les
              marchands sont partages : l'ecran semblait ne plus montrer de
              ventes par commercial. Ce classement repose sur ce que chacun a
              REELLEMENT vendu (commandes creditees a son nom). */}
          {(() => {
            const rank = [...(data.staff || [])].sort((a, b) => (b.ownRevenue || 0) - (a.ownRevenue || 0));
            const max = Math.max(1, ...rank.map((s) => Number(s.ownRevenue) || 0));
            const totalOwn = rank.reduce((a, s) => a + (Number(s.ownRevenue) || 0), 0);
            return (
              <div className="bg-white border border-gray-200 rounded-xl p-4 mb-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-bold text-gray-900">Ventes par commercial</h3>
                  <span className="text-xs text-gray-500">Total vendu par l'équipe : <strong className="text-gray-800">{fmt(totalOwn)} MAD</strong></span>
                </div>
                <div className="space-y-2.5">
                  {rank.map((s, i) => {
                    const v = Number(s.ownRevenue) || 0;
                    return (
                      <div key={s.id} className="grid grid-cols-12 items-center gap-3 text-sm">
                        <div className="col-span-12 sm:col-span-3 flex items-center gap-2 min-w-0">
                          <span className={`w-6 h-6 shrink-0 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 && v > 0 ? 'bg-[#e11d7b] text-white' : 'bg-gray-100 text-gray-600'}`}>{i + 1}</span>
                          <span className="font-medium text-gray-900 truncate">{s.name}</span>
                        </div>
                        <div className="col-span-7 sm:col-span-5">
                          <div className="h-2.5 rounded-full bg-gray-100 overflow-hidden">
                            <div className="h-full rounded-full bg-[#e11d7b]" style={{ width: `${Math.round((v / max) * 100)}%` }} />
                          </div>
                        </div>
                        <div className="col-span-5 sm:col-span-4 text-right whitespace-nowrap">
                          <span className="font-bold text-green-700">{fmt(v)} MAD</span>
                          <span className="text-xs text-gray-500"> · {fmt(s.ownOrdersCount || 0)} cmd · marge {fmt(s.ownMargin || 0)}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="text-[11px] text-gray-400 mt-3">
                  Commandes créditées au commercial (saisies par lui ou attribuées à son nom). Chaque vente n'est comptée qu'une fois.
                </p>
              </div>
            );
          })()}

          <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
            <table className="w-full text-sm min-w-[820px]">
              <thead>
                <tr className="text-left text-gray-500 border-b bg-gray-50">
                  <th className="py-3 px-4">Commercial</th>
                  <th className="py-3 px-4 text-right">Vendu par lui-même</th>
                  <th className="py-3 px-4 text-right">Marge Maxula</th>
                  <th className="py-3 px-4">Marchands suivis</th>
                  <th className="py-3 px-4">Annonces validées</th>
                  <th className="py-3 px-4">À valider</th>
                  <th className="py-3 px-4 text-right text-gray-400 font-normal">Ventes du portefeuille<div className="text-[10px]">(marchands partagés)</div></th>
                </tr>
              </thead>
              <tbody>
                {data.staff.map((s) => (
                  <React.Fragment key={s.id}>
                    <tr
                      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setOpenId(openId === s.id ? null : s.id)}
                    >
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          {openId === s.id ? <FiChevronDown size={14} /> : <FiChevronRight size={14} />}
                          <div>
                            <div className="font-medium text-gray-900">{s.name}</div>
                            <div className="text-xs text-gray-500">
                              {s.roleLabel || 'Aucun rôle'}
                              {s.status && s.status !== 'active' ? ' · compte suspendu' : ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      {/* VENDU PAR LUI-MEME : commandes qu'il a saisies au
                          guichet. Ne se recouvre avec aucun autre commercial,
                          contrairement a la colonne precedente. */}
                      <td className="py-3 px-4 text-right text-base font-bold text-green-700">
                        {fmt(s.ownRevenue ?? 0)} <span className="text-xs font-normal text-gray-400">MAD</span>
                        {s.ownOrdersCount ? (
                          <div className="text-xs font-normal text-gray-400">
                            {s.ownOrdersCount} commande{s.ownOrdersCount > 1 ? 's' : ''}
                          </div>
                        ) : null}
                      </td>
                      {/* MARGE : ce que Maxula garde apres avoir paye le
                          partenaire. Vendre 2 500 MAD d'offres a forte marge
                          et 2 500 MAD a faible marge n'a pas la meme valeur —
                          c'est cette colonne qui les distingue. */}
                      <td className="py-3 px-4 text-right font-bold text-emerald-700">
                        {fmt(s.ownMargin ?? 0)} <span className="text-xs font-normal text-gray-400">MAD</span>
                      </td>
                      <td className="py-3 px-4">
                        {s.scoped
                          ? <span className="font-semibold">{s.merchantCount}</span>
                          : <span className="text-xs text-gray-500">Tous (aucune restriction)</span>}
                      </td>
                      <td className="py-3 px-4">
                        <span className="font-semibold text-[#e11d7b]">{fmt(s.validatedDeals)}</span>
                        {s.rejectedDeals > 0 && (
                          <span className="text-xs text-gray-400"> · {s.rejectedDeals} refusée{s.rejectedDeals > 1 ? 's' : ''}</span>
                        )}
                      </td>
                      <td className="py-3 px-4">
                        {s.pendingDeals > 0
                          ? <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-xs font-semibold">{s.pendingDeals}</span>
                          : <span className="text-gray-400 text-xs">—</span>}
                      </td>
                      <td className="py-3 px-4 text-right text-gray-500">
                        {fmt(s.revenue)} <span className="text-xs font-normal text-gray-400">MAD</span>
                        {/* v1.453.0 — POURQUOI CE TOTAL EST LE MEME PARTOUT.
                            Quand tous les marchands qui vendent sont suivis par
                            plusieurs commerciaux, cette colonne affiche
                            forcement le meme montant sur chaque ligne : elle
                            mesure ce que LES MARCHANDS ont vendu, pas ce que le
                            commercial a vendu. Sans cette mention, l'ecran
                            donne l'impression d'afficher un total general. */}
                        {s.sharedMerchants > 0 && (
                          <div className="text-[10px] font-normal text-amber-600 mt-0.5">
                            {s.sharedMerchants} marchand{s.sharedMerchants > 1 ? 's' : ''} suivi
                            {s.sharedMerchants > 1 ? 's' : ''} aussi par d&apos;autres
                            {s.exclusiveMerchants > 0
                              ? ` · ${fmt(s.exclusiveRevenue ?? 0)} MAD en exclusivité`
                              : ' · aucun marchand exclusif'}
                          </div>
                        )}
                      <div className="text-[10px] text-gray-400">{fmt(s.ordersCount)} commande{s.ordersCount > 1 ? 's' : ''} du portefeuille</div>
                      </td>
                    </tr>

                    {openId === s.id && (
                      <tr className="bg-gray-50/60 border-b last:border-0">
                        <td colSpan={7} className="px-4 py-3">
                          {!s.scoped ? (
                            <p className="text-sm text-gray-500">
                              Aucun marchand coché : ce compte n'est pas limité à un portefeuille, il voit
                              tout ce que ses permissions autorisent.{' '}
                              <Link to="/admin/staff" className="text-[#e11d7b] underline">Lui affecter des marchands</Link>
                            </p>
                          ) : s.merchants.length === 0 ? (
                            <p className="text-sm text-gray-500">Portefeuille vide.</p>
                          ) : (
                            <table className="w-full text-xs">
                              <thead>
                                {/* v1.454.0 — CORRECTIF DE MISE EN PAGE.
                                    Les cellules n'avaient qu'un `py-1`, sans
                                    aucune marge horizontale. Avec 4 colonnes
                                    courtes cela passait ; en ajoutant
                                    « Vendu par lui », un montant aligne a
                                    droite s'est retrouve colle au nombre de la
                                    colonne suivante — on lisait
                                    « 6150 MAD23 » et l'en-tete affichait
                                    « Vendu par luiCommandes du marchand ».
                                    D'ou `px-3` partout et `whitespace-nowrap`
                                    sur les colonnes chiffrees. */}
                                <tr className="text-gray-500 text-left">
                                  <th className="py-1 px-3">Marchand</th>
                                  {/* « Vendu par lui » est la seule colonne qui
                                      differencie les commerciaux : celles du
                                      marchand sont identiques pour tous ceux
                                      qui le suivent. */}
                                  <th className="py-1 px-3 text-right whitespace-nowrap">Vendu par lui</th>
                                  <th className="py-1 px-3 text-right whitespace-nowrap">Commandes du marchand</th>
                                  <th className="py-1 px-3 whitespace-nowrap">À valider</th>
                                  <th className="py-1 px-3 text-right whitespace-nowrap">Ventes du marchand</th>
                                </tr>
                              </thead>
                              <tbody>
                                {s.merchants.map((m) => (
                                  <tr key={m.merchantId} className="border-t border-gray-200">
                                    <td className="py-1.5 px-3 text-gray-800">
                                      {m.name}
                                      {m.followers > 1 && (
                                        <span
                                          className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200"
                                          title="Ce marchand est suivi par plusieurs commerciaux : ses ventes apparaissent sur plusieurs lignes du tableau"
                                        >
                                          partagé × {m.followers}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1.5 px-3 text-right font-semibold text-primary-700 whitespace-nowrap">
                                      {fmt(m.ownRevenue ?? 0)} MAD
                                      {(m.ownOrders ?? 0) > 0 && (
                                        <span className="block text-[10px] font-normal text-gray-400">
                                          {m.ownOrders} commande{m.ownOrders > 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </td>
                                    <td className="py-1.5 px-3 text-right whitespace-nowrap">{fmt(m.orders)}</td>
                                    <td className="py-1.5 px-3">
                                      {m.pendingDeals > 0
                                        ? <span className="text-amber-600 font-semibold">{m.pendingDeals}</span>
                                        : <span className="text-gray-400">—</span>}
                                    </td>
                                    <td className="py-1.5 px-3 text-right text-green-700 whitespace-nowrap">{fmt(m.revenue)} MAD</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}

                {data.staff.length === 0 && (
                  <tr>
                    <td colSpan={7} className="py-8 text-center text-gray-400">
                      Aucun membre du personnel. Passez un utilisateur en « Personnel » depuis
                      Gestion des Utilisateurs.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-gray-400 mt-2">
            « Vendu par lui » (détail par marchand) = les ventes créditées à CE commercial chez CE
            marchand. C'est la colonne à regarder pour comparer deux commerciaux sur un même
            marchand. « Ventes du portefeuille » = tout ce que ses marchands ont vendu, tous canaux
            confondus. Un marchand suivi par plusieurs commerciaux apparaît chez chacun d'eux :
            cette colonne ne s'additionne donc pas. « Vendu par lui-même » = les commandes qu'il
            a saisies au guichet — sans recouvrement, c'est celle qui mesure son travail.
            Les vignettes du haut comptent chaque vente une seule fois.
            « Annonces validées » compte les décisions prises par le commercial lui-même sur la
            période.
          </p>
        </>
      )}
    </div>
  );
};

export default StaffPerformancePanel;
